<template>
  <div class="px-4">
    <PerformanceNavbar
      class="mb-8"
      :dateFilter="dateFilter"
      @update:dateFilter="switchPerformanceByDate"
      @update:performance="switchPerformanceById"
      :performancesByDate="performancesByFilterDate"
    />

    <template v-if="initialized">
      <PerformanceNoShowAlert
        v-if="noPerformancesByDateFilter"
        :dateFilter="dateFilter"
        @update:dateFilter="switchPerformanceByDate"
      />
      <div
        v-else
        class="flex flex-col justify-start space-y-8 lg:flex-row lg:space-y-0 lg:h-[75vh]"
      >
        <PerformanceRoomMap
          v-if="!isPerformanceDashboardPage"
          class="lg:w-[calc(100%-384px)]"
          @table:click="handleTableClick"
          :tables="tablesInRoomMap"
          :draggable="isPerformanceUpdateFlow"
        />
        <Transition
          enter-from-class="opacity-0"
          enter-active-class="ease-in-out duration-300"
          enter-to-class="opacity-100"
        >
          <div
            v-if="selectedPerformance && updatedPerformance"
            class="w-full lg:pr-2 lg:pl-6 lg:h-full lg:self-stretch tt-scroll"
            :class="[
              shouldPageViewHideScroll ? 'lg:overflow-visible' : 'lg:overflow-y-scroll',
              isPerformanceDashboardPage ? 'lg:w-full' : 'lg:w-[384px]',
            ]"
          >
            <router-view />
          </div>
        </Transition>
      </div>
      <PerformanceUnsavedChangesBar />

      <PerformanceUnsavedChangesDialog />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useEventBus } from '@vueuse/core'
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { reservationKey } from '@/api/events'
import { StorePerformance } from '@/models'
import PerformanceNoShowAlert from '@/modules/performance/components/molecules/PerformanceNoShowAlert.vue'
import PerformanceNavbar from '@/modules/performance/components/organisms/PerformanceNavbar.vue'
import PerformanceRoomMap from '@/modules/performance/components/organisms/PerformanceRoomMap.vue'
import PerformanceUnsavedChangesBar from '@/modules/performance/components/organisms/PerformanceUnsavedChangesBar.vue'
import PerformanceUnsavedChangesDialog from '@/modules/performance/components/organisms/PerformanceUnsavedChangesDialog.vue'
import {
  PERFORMANCE_FLOW_ROUTES,
  PERFORMANCE_ROUTE_NAMES,
  PerformanceRouteNames,
} from '@/modules/performance/constants/PerformanceRouteConstants'
import {
  PerformanceTableEvent,
  TABLE_UPDATE_STATUS,
} from '@/modules/performance/interfaces/PerformanceTableInterfaces'
import {
  PERFORMANCE_PARAMS,
  PerformanceTableWithReservations,
  usePerformanceReservationStore,
} from '@/modules/performance/stores/PerformanceReservationStore'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { usePerformanceUpdateStore } from '@/modules/performance/stores/PerformanceUpdateStore'
import { useSettingStore } from '@/stores/setting'
import { useTerminalStore } from '@/stores/terminal'
import { useUserStore } from '@/stores/user'
import { todayIsoStr } from '@/utils/utils'

import { PerformanceId, ReservationDetailedRead } from '@generated/types'

const terminalStore = useTerminalStore()
const userStore = useUserStore()
const performanceStore = usePerformanceStore()
const settingStore = useSettingStore()
const performanceUpdateStore = usePerformanceUpdateStore()
const { updatedPerformance, hasPerformanceUpdates } = storeToRefs(performanceUpdateStore)
const performanceReservationStore = usePerformanceReservationStore()
const {
  isLoadingSelectedPerformance,
  initialized,
  selectedPerformance,
  selectedReservation,
  activeReservationTableIds,
  tablesWithReservation,
  selectedTableIds,
} = storeToRefs(performanceReservationStore)

const props = defineProps<{ performanceId: PerformanceId }>()
const { performanceId: performanceIdParam } = toRefs(props)

onMounted(async () => {
  terminalStore.setTerminalPaymentStateNone()
  await Promise.all([
    userStore.fetchUsers(),
    performanceStore.getPerformances({ pagination: 'false', booking: 'false' }),
    performanceReservationStore.loadSelectedPerformance(performanceIdParam.value),
  ])
  if (selectedPerformance.value) {
    dateFilter.value = settingStore.dateIsoStr(selectedPerformance.value!.datetime)
  }
  if (performanceIdParam.value === PERFORMANCE_PARAMS.EMPTY && performancesByFilterDate.value[0]) {
    await router.replace({ params: { performanceId: performancesByFilterDate.value[0].id } })
  }
})

const isPerformanceDashboardPage = computed<boolean>(
  () => route.name === PERFORMANCE_ROUTE_NAMES.DASHBOARD,
)

// P E R F O R M A N C E    U P D A T E S   B Y   P A R A M S
watch(performanceIdParam, async function (newPerformanceId: PerformanceId) {
  await performanceReservationStore.loadSelectedPerformance(newPerformanceId)
})

// D A T E    F I L T E R    F U N C T I O N A L I T Y
const dateFilter = ref<string>(todayIsoStr())
async function switchPerformanceByDate(date: string) {
  const firstPerformance = _.head<StorePerformance>(getPerformancesByDate(date))
  const newPerformanceId = firstPerformance?.id || PERFORMANCE_PARAMS.EMPTY
  if (!hasPerformanceUpdates.value) dateFilter.value = date
  const response = await router.replace({ params: { performanceId: newPerformanceId } })
  // We did navigate to the new route, so we need to update the date filter
  if (response === undefined) dateFilter.value = date
}
async function switchPerformanceById(performanceId: PerformanceId) {
  await router.replace({ params: { performanceId } })
  const performance = _.find(performanceStore.performances, { id: performanceId })
  if (performance) {
    dateFilter.value = settingStore.dateIsoStr(performance.datetime)
  }
}
function getPerformancesByDate(date: string): StorePerformance[] {
  const performanceList = _.filter(performanceStore.performances, { date })
  return _.sortBy<StorePerformance>(performanceList, ['datetime', 'show.name'])
}
const performancesByFilterDate = computed<StorePerformance[]>(() =>
  getPerformancesByDate(dateFilter.value),
)
const noPerformancesByDateFilter = computed<boolean>(
  () => performanceIdParam.value === PERFORMANCE_PARAMS.EMPTY,
)
const route = useRoute()
const shouldPageViewHideScroll = computed<boolean>(() => {
  return (
    route.name === PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL ||
    route.name === PERFORMANCE_ROUTE_NAMES.RESERVATION_EDIT ||
    route.name === PERFORMANCE_ROUTE_NAMES.DASHBOARD
  )
})

// W E B    S O C K E T    E V E N T S
const bus = useEventBus(reservationKey)
async function onReservationChange(reservation: ReservationDetailedRead) {
  if (!selectedPerformance.value || reservation.performance_id !== selectedPerformance.value.id) {
    return
  }
  if (selectedReservation.value && selectedReservation.value.id === reservation.id) {
    performanceReservationStore.setSelectedReservation(reservation)
  }
  await performanceReservationStore.backgroundRefresh()
}
const unsubscribe = bus.on(onReservationChange)
onUnmounted(unsubscribe)

// R O O M    M A P    T A B L E    C L I C K
const { isPayingInTerminal } = storeToRefs(useTerminalStore())
const router = useRouter()
function handleTableClick(tableEvent: PerformanceTableEvent) {
  const routeName = route.name as PerformanceRouteNames
  switch (routeName) {
    case PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST:
      if (tableEvent.activeReservationId === null) {
        performanceReservationStore.walkInTable(tableEvent)
        router.push({ name: PERFORMANCE_ROUTE_NAMES.RESERVATION_WALK_IN })
      } else {
        router.push({
          name: PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL,
          params: { reservationId: tableEvent.activeReservationId },
        })
      }
      break
    case PERFORMANCE_ROUTE_NAMES.RESERVATION_WALK_IN:
      if (isPayingInTerminal.value) return
      performanceReservationStore.walkInTable(tableEvent)
      break
    case PERFORMANCE_ROUTE_NAMES.RESERVATION_EDIT:
      performanceReservationStore.toggleReservationTable(tableEvent)
      break
    case PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL:
      if (_.includes(selectedReservation.value?.tables, tableEvent.tableId)) {
        router.push({ name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST })
      } else if (tableEvent.activeReservationId !== null) {
        router.push({
          name: PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL,
          params: { reservationId: tableEvent.activeReservationId },
        })
      }
      break
    default:
      if (_.values(PERFORMANCE_FLOW_ROUTES).includes(routeName)) {
        performanceReservationStore.setSelectedTableIds([tableEvent.tableId])
        router.replace({
          name: PERFORMANCE_ROUTE_NAMES.TABLE_EDIT,
          params: { tableId: tableEvent.tableId },
        })
      }
      break
  }
}

// R O O M    M A P    T A B L E    U I
const isPerformanceUpdateFlow = computed<boolean>(() => {
  return _.values(PERFORMANCE_FLOW_ROUTES).includes(route.name as PerformanceRouteNames)
})
const tablesInRoomMap = computed<PerformanceTableWithReservations[]>(() => {
  if (isLoadingSelectedPerformance.value || !selectedPerformance.value || !updatedPerformance.value)
    return []
  const tablesToDisplay = isPerformanceUpdateFlow.value
    ? performanceUpdateStore.tablesWithUpdates
    : performanceReservationStore.tablesWithReservation
  return _.filter(tablesToDisplay, { hidden: false })
})
function fillTablesWithActiveReservation() {
  performanceReservationStore.setFilledTableIds(
    _.map(
      _.reject(tablesInRoomMap.value, { reservationStatus: TABLE_UPDATE_STATUS.AVAILABLE }),
      'id',
    ),
  )
}
watch(
  [() => route.name, selectedTableIds, activeReservationTableIds, tablesInRoomMap],
  ([newRouteName]) => {
    switch (newRouteName as PerformanceRouteNames) {
      case PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST:
        performanceReservationStore.setFilledTableIds(activeReservationTableIds.value)
        performanceReservationStore.setLowOpacityTableIds([])
        break
      case PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL:
        performanceReservationStore.setFilledTableIds(activeReservationTableIds.value)
        performanceReservationStore.setLowOpacityTableIds(
          _.difference(
            _.map(tablesWithReservation.value, 'id'),
            selectedReservation.value?.tables || [],
          ),
        )
        break
      case PERFORMANCE_ROUTE_NAMES.RESERVATION_EDIT:
        performanceReservationStore.setFilledTableIds(
          _.concat(
            _.difference(activeReservationTableIds.value, selectedReservation.value?.tables || []),
            selectedTableIds.value,
          ),
        )
        performanceReservationStore.setLowOpacityTableIds(
          _.difference(activeReservationTableIds.value, selectedReservation.value?.tables || []),
        )
        break
      case PERFORMANCE_ROUTE_NAMES.RESERVATION_WALK_IN:
        performanceReservationStore.setFilledTableIds(
          _.concat(selectedTableIds.value, activeReservationTableIds.value),
        )
        performanceReservationStore.setLowOpacityTableIds(
          _.difference(activeReservationTableIds.value, selectedTableIds.value),
        )
        break
      case PERFORMANCE_ROUTE_NAMES.TABLE_EDIT:
        fillTablesWithActiveReservation()
        performanceReservationStore.setLowOpacityTableIds(
          _.difference(_.map(updatedPerformance.value?.tables || [], 'id'), selectedTableIds.value),
        )
        break
      default:
        fillTablesWithActiveReservation()
        performanceReservationStore.setLowOpacityTableIds([])
        break
    }
  },
  { immediate: true, deep: true },
)
</script>
